import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Trash2, Save, Dumbbell, Salad, Bell, User, Ruler, Shield, Eye, X, Edit2, Check, ShoppingCart, ChevronDown, Calendar as CalendarIcon, Flame, FileDown } from 'lucide-react';
import CalendarView from '../components/CalendarView';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const BACKEND_URL = window.location.hostname === 'localhost'
  ? 'http://localhost:8000'
  : (process.env.REACT_APP_BACKEND_URL || '');

const AVATAR_COLORS = [
  '#4F46E5','#7C3AED','#DB2777','#DC2626','#D97706',
  '#059669','#0891B2','#2563EB','#9333EA','#C2410C',
];
function nameToColor(name = '') {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}
function nameInitials(name = '') {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const DAYS = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];

const emptyExercise = () => ({ name: '', series: '', reps: '', weight: '', rest: '', notes: '' });
const emptyFood     = () => ({ name: '', quantity: '', calories: '', protein: '', carbs: '', fat: '' });
const emptyMeal     = () => ({ name: '', time: '', foods: [emptyFood()] });

export default function AdminAluno() {
  const { id } = useParams();
  const navigate = useNavigate();
  const token = sessionStorage.getItem('admin_token');

  const [student, setStudent] = useState(null);
  const [tab, setTab] = useState('profile');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savedAssignmentId, setSavedAssignmentId] = useState(null);

  const [workout, setWorkout] = useState({
    name: 'Meu Treino', days: [], exercises: [emptyExercise()], notes: '',
  });

  const [diet, setDiet] = useState({
    name: 'Minha Dieta', calories: '', meals: [emptyMeal()], notes: '',
  });

  const [workoutAssignments, setWorkoutAssignments] = useState([]);
  const [dietAssignments, setDietAssignments] = useState([]);
  const [workoutTemplates, setWorkoutTemplates] = useState([]);
  const [workoutPrograms, setWorkoutPrograms] = useState([]);
  const [dietTemplates, setDietTemplates] = useState([]);
  const [shoppingList, setShoppingList] = useState(null);
  const [showWorkoutPanel, setShowWorkoutPanel] = useState(false);
  const [showDietPanel, setShowDietPanel] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [workoutAssignForm, setWorkoutAssignForm] = useState({ name: '', sourceType: 'template', templateId: '', programId: '', startDate: '', endDate: '', scheduledDays: [], timeOfDay: 'Manhã', exercises: [] });
  const [dietAssignForm, setDietAssignForm] = useState({ name: '', templateId: '', startDate: '', endDate: '', meals: [] });
  const [shoppingPeriod, setShoppingPeriod] = useState('semanal');
  const [shoppingLoading, setShoppingLoading] = useState(false);

  const [notify, setNotify] = useState({ title: '', body: '' });
  const [notifySending, setNotifySending] = useState(false);
  const [notifyOk, setNotifyOk] = useState(false);

  const [profile, setProfile] = useState({ birthdate: '', phone: '', goal: '', medicalNotes: '' });
  const [newMeasurement, setNewMeasurement] = useState({ weight: '', height: '', bodyFat: '', muscleMass: '', waist: '', hip: '', notes: '', date: '' });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [showMeasForm, setShowMeasForm] = useState(false);
  const [expandedWorkoutInProfile, setExpandedWorkoutInProfile] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [alertMsg, setAlertMsg] = useState('');
  const [assignMode, setAssignMode] = useState('predefined'); // 'predefined' | 'manual'
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [newAssignment, setNewAssignment] = useState({
    type: 'treino', name: '', workoutTemplateName: '',
    startDate: '', endDate: '',
    scheduledDays: [], timeOfDay: 'Manhã',
    exercises: [], treinos: [],
  });
  const [selectedPeriodizacaoId, setSelectedPeriodizacaoId] = useState('');
  const [expandedTreino, setExpandedTreino] = useState(null); // `${assignId}-${treinoIdx}`
  const [expandedAssignment, setExpandedAssignment] = useState(null);
  const [expandedDietAssignment, setExpandedDietAssignment] = useState(null);
  const [savedDietAssignmentId, setSavedDietAssignmentId] = useState(null);
  const [editingEmail, setEditingEmail] = useState(false);
  const [emailDraft, setEmailDraft] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const [sRes, wRes, dRes, pRes, waRes, daRes, wtRes, wpRes, dtRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/students`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/students/${id}/workout`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/students/${id}/diet`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/students/${id}/profile`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/students/${id}/workout-assignments`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/admin/students/${id}/diet-assignments`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/admin/workout-templates`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/admin/workout-programs`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${BACKEND_URL}/api/admin/diet-templates`, { headers }).catch(() => ({ data: [] })),
      ]);
      const found = sRes.data.find(s => s.id === id);
      setStudent(found || { name: 'Aluno', email: '' });
      if (wRes.data) setWorkout({ exercises: [emptyExercise()], days: [], notes: '', ...wRes.data });
      if (dRes.data) setDiet({ meals: [emptyMeal()], notes: '', ...dRes.data });
      if (pRes.data) setProfile(p => ({ ...p, ...pRes.data }));
      if (found) {
        setProfile(p => ({
          ...p,
          status: found.status || 'pending',
          permissions: found.permissions || { workout: false, diet: false, content: false, ebooks: false, measurements: false },
        }));
      }
      setWorkoutAssignments(waRes.data || []);
      setDietAssignments(daRes.data || []);
      setWorkoutTemplates(wtRes.data || []);
      setWorkoutPrograms(wpRes.data || []);
      setDietTemplates(dtRes.data || []);
    } catch { navigate('/admin'); }
  // eslint-disable-next-line
  }, [id]);

  useEffect(() => { if (!token) { navigate('/admin'); return; } load(); }, [load, token, navigate]);

  const saveWorkout = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/students/${id}/workout`, workout, { headers });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch { setAlertMsg('Erro ao salvar treino.'); }
    finally { setSaving(false); }
  };

  const saveDiet = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/students/${id}/diet`, diet, { headers });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch { setAlertMsg('Erro ao salvar dieta.'); }
    finally { setSaving(false); }
  };

  // ── Workout helpers ──
  const setEx = (i, field, val) => setWorkout(w => ({ ...w, exercises: w.exercises.map((e, j) => j === i ? { ...e, [field]: val } : e) }));
  const addEx = () => setWorkout(w => ({ ...w, exercises: [...w.exercises, emptyExercise()] }));
  const removeEx = i => setWorkout(w => ({ ...w, exercises: w.exercises.filter((_, j) => j !== i) }));
  const toggleDay = d => setWorkout(w => ({ ...w, days: w.days.includes(d) ? w.days.filter(x => x !== d) : [...w.days, d] }));

  // ── Diet helpers ──
  const setMeal = (i, field, val) => setDiet(d => ({ ...d, meals: d.meals.map((m, j) => j === i ? { ...m, [field]: val } : m) }));
  const addMeal = () => setDiet(d => ({ ...d, meals: [...d.meals, emptyMeal()] }));
  const removeMeal = i => setDiet(d => ({ ...d, meals: d.meals.filter((_, j) => j !== i) }));
  const setFood = (mi, fi, field, val) => setDiet(d => ({ ...d, meals: d.meals.map((m, j) => j === mi ? { ...m, foods: m.foods.map((f, k) => k === fi ? { ...f, [field]: val } : f) } : m) }));
  const addFood = mi => setDiet(d => ({ ...d, meals: d.meals.map((m, j) => j === mi ? { ...m, foods: [...m.foods, emptyFood()] } : m) }));
  const removeFood = (mi, fi) => setDiet(d => ({ ...d, meals: d.meals.map((m, j) => j === mi ? { ...m, foods: m.foods.filter((_, k) => k !== fi) } : m) }));

  const saveProfile = async () => {
    setProfileSaving(true);
    try {
      const { status, permissions, ...profileData } = profile;
      await Promise.all([
        axios.put(`${BACKEND_URL}/api/admin/students/${id}/profile`, profileData, { headers }),
        axios.patch(`${BACKEND_URL}/api/admin/students/${id}/permissions`, { status, permissions }, { headers }),
      ]);
      setProfileSaved(true); setTimeout(() => setProfileSaved(false), 2000);
      // After saving, if student is still pending or inactive, prompt activation
      if (status === 'pending' || status === 'inactive') {
        const label = status === 'pending' ? 'Aguardando' : 'Inativo';
        setConfirm({
          message: `Aluno está como "${label}". Deseja ativar o acesso agora?`,
          confirmLabel: 'Ativar agora',
          confirmStyle: 'success',
          altLabel: 'Ativar depois',
          onConfirm: async () => {
            try {
              await axios.patch(`${BACKEND_URL}/api/admin/students/${id}/permissions`, { status: 'active', permissions }, { headers });
              setProfile(p => ({ ...p, status: 'active' }));
            } catch { setAlertMsg('Erro ao ativar aluno.'); }
          },
          onAlt: () => { /* keep as pending/inactive, just close */ },
        });
      }
    } catch { setAlertMsg('Erro ao salvar perfil.'); }
    finally { setProfileSaving(false); }
  };

  const addMeasurement = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/students/${id}/profile/measurements`, newMeasurement, { headers });
      setProfile(p => ({ ...p, measurements: res.data.measurements }));
      setNewMeasurement({ weight: '', height: '', bodyFat: '', muscleMass: '', waist: '', hip: '', notes: '', date: '' });
      setShowMeasForm(false);
    } catch { setAlertMsg('Erro ao adicionar medida.'); }
  };

  const removeMeasurement = (index) => {
    setConfirm({
      message: 'Remover esta medição?',
      onConfirm: async () => {
        try {
          const res = await axios.delete(`${BACKEND_URL}/api/admin/students/${id}/profile/measurements/${index}`, { headers });
          setProfile(p => ({ ...p, measurements: res.data.measurements }));
        } catch { setAlertMsg('Erro ao remover medida.'); }
      },
    });
  };

  const updateAssignmentExercise = (assignmentIdx, exerciseIdx, field, value) => {
    setWorkoutAssignments(prev => prev.map((a, ai) => ai === assignmentIdx ? {
      ...a,
      exercises: a.exercises.map((ex, ei) => ei === exerciseIdx ? { ...ex, [field]: value } : ex)
    } : a));
  };

  const addExerciseToAssignment = (assignmentIdx) => {
    setWorkoutAssignments(prev => prev.map((a, ai) => ai === assignmentIdx ? {
      ...a,
      exercises: [...(a.exercises || []), { name: '', prescribedSeries: '', prescribedReps: '', prescribedWeight: '', rest: '', notes: '', actualWeight: '', actualReps: '', actualSeries: '', studentNotes: '' }]
    } : a));
  };

  const emptyExerciseItem = () => ({ name:'', prescribedSeries:'', prescribedReps:'', prescribedWeight:'', rest:'', notes:'', actualWeight:'', actualReps:'', actualSeries:'', studentNotes:'', exerciseType:'musculação', estimatedCalories:'' });
  const emptyTreinoItem = () => ({ id: Date.now().toString(), name:'', startDate:'', endDate:'', scheduledDays:[], timeOfDay:'Manhã', exercises:[] });

  const updateTreinoField = (ti, field, val) =>
    setNewAssignment(a => ({ ...a, treinos: a.treinos.map((t,i) => i===ti ? {...t,[field]:val} : t) }));
  const updateTreinoExercise = (ti, ei, field, val) =>
    setNewAssignment(a => ({ ...a, treinos: a.treinos.map((t,i) => i===ti ? {...t, exercises: t.exercises.map((x,j) => j===ei ? {...x,[field]:val} : x)} : t) }));
  const updateAssignmentTreinoExercise = (ai, ti, ei, field, val) =>
    setWorkoutAssignments(prev => prev.map((a,i) => i===ai ? {...a, treinos: a.treinos.map((t,j) => j===ti ? {...t, exercises: t.exercises.map((x,k) => k===ei ? {...x,[field]:val} : x)} : t)} : a));

  const saveAssignmentExercises = async (assignmentId, exercises) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/students/${id}/workout-assignments/${assignmentId}`, { exercises }, { headers });
      setSavedAssignmentId(assignmentId);
      setTimeout(() => setSavedAssignmentId(null), 2000);
    } catch { setAlertMsg('Erro ao salvar treino.'); }
  };

  // Diet assignment inline editing helpers
  const updateDietAssignmentMeal = (ai, mi, field, val) =>
    setDietAssignments(prev => prev.map((a, i) => i === ai ? {
      ...a, meals: a.meals.map((m, j) => j === mi ? { ...m, [field]: val } : m)
    } : a));

  const updateDietAssignmentFood = (ai, mi, fi, field, val) =>
    setDietAssignments(prev => prev.map((a, i) => i === ai ? {
      ...a, meals: a.meals.map((m, j) => j === mi ? {
        ...m, foods: m.foods.map((f, k) => k === fi ? { ...f, [field]: val } : f)
      } : m)
    } : a));

  const ALL_DAYS = ['Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado', 'Domingo'];

  const addDietAssignmentMeal = (ai) =>
    setDietAssignments(prev => prev.map((a, i) => i === ai ? {
      ...a, meals: [...(a.meals || []), { name: '', time: '', days: [...ALL_DAYS], foods: [{ name: '', quantity: '', calories: '', protein: '', carbs: '', fat: '', preparation: 'cru' }] }]
    } : a));

  const removeDietAssignmentMeal = (ai, mi) =>
    setDietAssignments(prev => prev.map((a, i) => i === ai ? {
      ...a, meals: a.meals.filter((_, j) => j !== mi)
    } : a));

  const addDietAssignmentFood = (ai, mi) =>
    setDietAssignments(prev => prev.map((a, i) => i === ai ? {
      ...a, meals: a.meals.map((m, j) => j === mi ? {
        ...m, foods: [...(m.foods || []), { name: '', quantity: '', calories: '', protein: '', carbs: '', fat: '' }]
      } : m)
    } : a));

  const removeDietAssignmentFood = (ai, mi, fi) =>
    setDietAssignments(prev => prev.map((a, i) => i === ai ? {
      ...a, meals: a.meals.map((m, j) => j === mi ? {
        ...m, foods: m.foods.filter((_, k) => k !== fi)
      } : m)
    } : a));

  const saveDietAssignment = async (assignmentId, assignment) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/students/${id}/diet-assignments/${assignmentId}`, assignment, { headers });
      setSavedDietAssignmentId(assignmentId);
      setTimeout(() => setSavedDietAssignmentId(null), 2000);
    } catch { setAlertMsg('Erro ao salvar dieta.'); }
  };

  const saveStudentEmail = async () => {
    if (!emailDraft.trim()) return;
    try {
      await axios.put(`${BACKEND_URL}/api/admin/students/${id}/email`, { email: emailDraft.trim() }, { headers });
      setStudent(s => ({ ...s, email: emailDraft.trim() }));
      setEditingEmail(false);
    } catch (err) { setAlertMsg(err.response?.data?.detail || 'Erro ao atualizar e-mail.'); }
  };

  const sendNotify = async (e) => {
    e.preventDefault();
    if (!notify.title) return;
    setNotifySending(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/students/${id}/notify`, notify, { headers });
      setNotifyOk(true);
      setNotify({ title: '', body: '' });
      setTimeout(() => setNotifyOk(false), 3000);
    } catch { setAlertMsg('Erro ao enviar notificação. O aluno pode não ter notificações ativas.'); }
    finally { setNotifySending(false); }
  };

  const exportDietPDF = () => {
    const doc = new jsPDF();
    const today = new Date().toLocaleDateString('pt-BR');
    const studentName = student?.name || 'Aluno';
    const safeDate = today.replace(/\//g, '-');

    doc.setFontSize(16);
    doc.text('Plano Alimentar', 14, 18);
    doc.setFontSize(11);
    doc.text(`Aluno: ${studentName}`, 14, 26);
    doc.text(`Data: ${today}`, 14, 32);

    let y = 42;

    dietAssignments.forEach((assignment, ai) => {
      if (ai > 0) { doc.addPage(); y = 18; }
      doc.setFontSize(13);
      doc.setFont(undefined, 'bold');
      doc.text(assignment.name || `Dieta ${ai + 1}`, 14, y);
      doc.setFont(undefined, 'normal');
      y += 8;

      if (assignment.startDate || assignment.endDate) {
        doc.setFontSize(9);
        doc.text(`Período: ${assignment.startDate || '?'} — ${assignment.endDate || 'sem prazo'}`, 14, y);
        y += 6;
      }

      (assignment.meals || []).forEach(meal => {
        const mealTotal = (meal.foods || []).reduce((acc, f) => ({
          cal: acc.cal + (parseFloat(f.calories) || 0),
          prot: acc.prot + (parseFloat(f.protein) || 0),
          carbs: acc.carbs + (parseFloat(f.carbs) || 0),
          fat: acc.fat + (parseFloat(f.fat) || 0),
        }), { cal: 0, prot: 0, carbs: 0, fat: 0 });

        doc.setFontSize(11);
        doc.setFont(undefined, 'bold');
        const mealLabel = `${meal.name || 'Refeição'}${meal.time ? ' — ' + meal.time : ''}`;
        doc.text(mealLabel, 14, y);
        doc.setFont(undefined, 'normal');
        y += 5;

        const rows = (meal.foods || []).map(f => [
          f.name || '',
          f.quantity || '',
          f.preparation || '',
          f.calories ? `${f.calories} kcal` : '',
          f.protein ? `${f.protein}g` : '',
          f.carbs ? `${f.carbs}g` : '',
          f.fat ? `${f.fat}g` : '',
        ]);

        autoTable(doc, {
          startY: y,
          head: [['Alimento', 'Qtd', 'Estado', 'Kcal', 'Prot', 'Carbs', 'Gord']],
          body: rows,
          foot: [['Total', '', '', `${mealTotal.cal.toFixed(0)} kcal`, `${mealTotal.prot.toFixed(1)}g`, `${mealTotal.carbs.toFixed(1)}g`, `${mealTotal.fat.toFixed(1)}g`]],
          theme: 'striped',
          headStyles: { fillColor: [20, 20, 20], textColor: 255, fontSize: 8 },
          footStyles: { fillColor: [40, 40, 40], textColor: 255, fontSize: 8, fontStyle: 'bold' },
          bodyStyles: { fontSize: 8 },
          margin: { left: 14, right: 14 },
        });
        y = doc.lastAutoTable.finalY + 8;
        if (y > 260) { doc.addPage(); y = 18; }
      });
    });

    doc.save(`dieta_${studentName.replace(/\s+/g, '_')}_${safeDate}.pdf`);
  };

  const exportWorkoutPDF = () => {
    const doc = new jsPDF();
    const today = new Date().toLocaleDateString('pt-BR');
    const studentName = student?.name || 'Aluno';
    const safeDate = today.replace(/\//g, '-');

    doc.setFontSize(16);
    doc.text('Plano de Treino', 14, 18);
    doc.setFontSize(11);
    doc.text(`Aluno: ${studentName}`, 14, 26);
    doc.text(`Data: ${today}`, 14, 32);

    let y = 42;

    workoutAssignments.forEach((assignment, ai) => {
      if (ai > 0) { doc.addPage(); y = 18; }
      doc.setFontSize(13);
      doc.setFont(undefined, 'bold');
      doc.text(assignment.workoutTemplateName || assignment.name || `Treino ${ai + 1}`, 14, y);
      doc.setFont(undefined, 'normal');
      y += 8;

      if (assignment.startDate || assignment.endDate) {
        doc.setFontSize(9);
        doc.text(`Período: ${assignment.startDate || '?'} — ${assignment.endDate || 'sem prazo'}`, 14, y);
        y += 5;
      }
      if (assignment.scheduledDays?.length > 0) {
        doc.setFontSize(9);
        doc.text(`Dias: ${assignment.scheduledDays.join(', ')}`, 14, y);
        y += 6;
      }

      const renderExercises = (exercises) => {
        if (!exercises?.length) return;
        const rows = exercises.map(ex => [
          ex.name || '',
          ex.prescribedSeries || ex.series || '',
          ex.prescribedReps || ex.reps || '',
          ex.prescribedWeight || ex.weight || '',
          ex.rest || '',
          ex.notes || '',
        ]);
        autoTable(doc, {
          startY: y,
          head: [['Exercício', 'Séries', 'Reps', 'Carga', 'Descanso', 'Obs']],
          body: rows,
          theme: 'striped',
          headStyles: { fillColor: [20, 20, 20], textColor: 255, fontSize: 8 },
          bodyStyles: { fontSize: 8 },
          margin: { left: 14, right: 14 },
        });
        y = doc.lastAutoTable.finalY + 8;
        if (y > 260) { doc.addPage(); y = 18; }
      };

      if (assignment.type === 'periodizacao') {
        (assignment.treinos || []).forEach((treino, ti) => {
          doc.setFontSize(11);
          doc.setFont(undefined, 'bold');
          doc.text(treino.name || `Treino ${ti + 1}`, 14, y);
          doc.setFont(undefined, 'normal');
          y += 5;
          renderExercises(treino.exercises);
        });
      } else {
        renderExercises(assignment.exercises);
      }
    });

    doc.save(`treino_${studentName.replace(/\s+/g, '_')}_${safeDate}.pdf`);
  };

  const inputCls = 'bg-[#0A0A0A] border border-white/10 focus:border-white/30 outline-none px-3 py-2 text-sm w-full transition-colors';
  const labelCls = 'block text-xs text-[#555] uppercase tracking-widest mb-1';

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-6 lg:pt-10 pb-20">
      <div className="max-w-6xl mx-auto px-4 md:px-8">

        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button onClick={() => navigate('/admin')} className="text-[#555] hover:text-white transition-colors">
            <ArrowLeft size={20} />
          </button>
          <div
            className="w-10 h-10 flex-shrink-0 flex items-center justify-center text-white text-sm font-bold select-none"
            style={{ backgroundColor: nameToColor(student?.name || '') }}
            title={student?.name}
          >
            {nameInitials(student?.name || '')}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold uppercase tracking-tight">{student?.name}</h1>
              {profile.status && (
                <span className={`text-xs border px-2 py-0.5 ${
                  profile.status === 'active' ? 'text-green-400 border-green-500/30' :
                  profile.status === 'pending' ? 'text-yellow-400 border-yellow-400/30' :
                  'text-red-400 border-red-500/30'
                }`}>
                  {profile.status === 'active' ? 'Ativo' : profile.status === 'pending' ? 'Aguardando' : 'Inativo'}
                </span>
              )}
            </div>
            {editingEmail ? (
              <div className="flex items-center gap-1 mt-0.5">
                <input
                  autoFocus
                  value={emailDraft}
                  onChange={e => setEmailDraft(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveStudentEmail(); if (e.key === 'Escape') setEditingEmail(false); }}
                  className="bg-[#121212] border border-white/20 px-2 py-0.5 text-xs outline-none w-52"
                />
                <button onClick={saveStudentEmail} className="text-green-400 hover:text-green-300"><Check size={13} /></button>
                <button onClick={() => setEditingEmail(false)} className="text-[#555] hover:text-white"><X size={13} /></button>
              </div>
            ) : (
              <div className="flex items-center gap-1.5 mt-0.5">
                <p className="text-xs text-[#555]">{student?.email}</p>
                <button onClick={() => { setEmailDraft(student?.email || ''); setEditingEmail(true); }} className="text-[#333] hover:text-[#888] transition-colors"><Edit2 size={11} /></button>
              </div>
            )}
          </div>
          <button
            onClick={async () => {
              try {
                const res = await axios.get(`${BACKEND_URL}/api/admin/students/${id}/preview-token`, { headers });
                window.open(`/preview/${id}?token=${res.data.token}`, '_blank');
              } catch { setAlertMsg('Erro ao gerar token de preview.'); }
            }}
            className="flex items-center gap-2 px-3 py-1.5 bg-[#121212] border border-white/10 hover:border-white/30 text-xs transition-colors ml-auto"
            title="Visualizar como aluno"
          >
            <Eye size={14} /> Visão do aluno
          </button>
        </div>

        {/* Enviar notificação */}
        <div className="bg-[#121212] border border-white/5 p-5 mb-8">
          <h3 className="text-xs font-bold uppercase tracking-widest text-[#A3A3A3] mb-4 flex items-center gap-2">
            <Bell size={13} /> Enviar notificação push
          </h3>
          <form onSubmit={sendNotify} className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[160px]">
              <label className={labelCls}>Título</label>
              <input type="text" required value={notify.title} placeholder="Ex: Treino atualizado!"
                onChange={e => setNotify(n => ({ ...n, title: e.target.value }))}
                className={inputCls}
              />
            </div>
            <div className="flex-[2] min-w-[200px]">
              <label className={labelCls}>Mensagem (opcional)</label>
              <input type="text" value={notify.body} placeholder="Ex: Confira seu novo plano na área do aluno."
                onChange={e => setNotify(n => ({ ...n, body: e.target.value }))}
                className={inputCls}
              />
            </div>
            <button type="submit" disabled={notifySending}
              className="px-5 py-2 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors disabled:opacity-50"
            >
              {notifySending ? 'Enviando...' : 'Enviar'}
            </button>
          </form>
          {notifyOk && (
            <p className="mt-3 text-green-400 text-sm">Notificação enviada com sucesso.</p>
          )}
        </div>

        {/* Tabs */}
        <div className="flex border-b border-white/10 mb-8 overflow-x-auto hide-scrollbar">
          {[['profile', <User size={14} />, 'Perfil'], ['workout', <Dumbbell size={14} />, 'Treino'], ['diet', <Salad size={14} />, 'Dieta'], ['measurements', <Ruler size={14} />, 'Medidas'], ['calendar', <CalendarIcon size={14} />, 'Calendário']].map(([key, icon, label]) => (
            <button key={key} onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-4 py-3 text-xs sm:text-sm font-medium uppercase tracking-wider transition-colors border-b-2 -mb-px whitespace-nowrap ${tab === key ? 'text-white border-white' : 'text-[#555] border-transparent hover:text-[#A3A3A3]'}`}
            >
              {icon} {label}
            </button>
          ))}
        </div>

        {/* ── TREINO ── */}
        {tab === 'workout' && (
          <div className="flex flex-col gap-6">

            {/* Section 1: Treinos atribuídos */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xs font-bold uppercase tracking-widest text-[#A3A3A3]">Treinos atribuídos</h2>
                <div className="flex items-center gap-2">
                  {workoutAssignments.length > 0 && (
                    <button onClick={exportWorkoutPDF}
                      className="flex items-center gap-2 px-4 py-2 bg-[#121212] border border-white/10 text-xs font-bold uppercase tracking-wider hover:border-white/30 transition-colors">
                      <FileDown size={13} /> Exportar PDF
                    </button>
                  )}
                  <button onClick={() => { setShowWorkoutPanel(true); setNewAssignment({ type:'treino', name:'', workoutTemplateName:'', startDate:'', endDate:'', scheduledDays:[], timeOfDay:'Manhã', exercises:[], treinos:[] }); setSelectedTemplateId(''); setSelectedPeriodizacaoId(''); setAssignMode('predefined'); }}
                    className="flex items-center gap-2 px-4 py-2 bg-white text-black text-xs font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors">
                    <Plus size={13} /> Atribuir treino
                  </button>
                </div>
              </div>

              {workoutAssignments.length === 0 ? (
                <div className="text-center py-8 text-[#555] text-sm bg-[#121212] border border-white/5">
                  Nenhuma atribuição de treino ainda.
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {workoutAssignments.map((assignment, ai) => {
                    const today = new Date().toISOString().slice(0, 10);
                    let badgeCls = 'text-green-400 border-green-500/30';
                    let badgeLabel = 'Ativo';
                    if (assignment.endDate && assignment.endDate < today) { badgeCls = 'text-[#555] border-white/10'; badgeLabel = 'Encerrado'; }
                    else if (assignment.startDate && assignment.startDate > today) { badgeCls = 'text-yellow-400 border-yellow-400/30'; badgeLabel = 'Próximo'; }
                    const isExpanded = expandedAssignment === assignment.id;
                    return (
                      <div key={assignment.id} className="bg-[#121212] border border-white/5">
                        <div
                          className="px-5 py-4 flex items-center justify-between gap-3 cursor-pointer"
                          onClick={() => setExpandedAssignment(isExpanded ? null : assignment.id)}
                        >
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-medium text-sm">{assignment.workoutTemplateName || assignment.name || 'Treino'}</span>
                              {assignment.type === 'periodizacao' && <span className="text-xs border border-purple-400/30 text-purple-400 px-2 py-0.5">Periodização</span>}
                              <span className={`text-xs border px-2 py-0.5 ${badgeCls}`}>{badgeLabel}</span>
                            </div>
                            <p className="text-xs text-[#555] mt-0.5">
                              {assignment.startDate || '?'} — {assignment.endDate || 'sem prazo'}
                              {assignment.scheduledDays?.length > 0 && ` · ${assignment.scheduledDays.join(', ')}`}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button onClick={e => { e.stopPropagation(); setConfirm({ message: 'Remover esta atribuição? Esta ação não pode ser desfeita.', onConfirm: async () => { try { await axios.delete(`${BACKEND_URL}/api/admin/students/${id}/workout-assignments/${assignment.id}`, { headers }); setWorkoutAssignments(prev => prev.filter(a => a.id !== assignment.id)); } catch { setAlertMsg('Erro ao remover.'); } } }); }} className="text-[#555] hover:text-red-400 transition-colors"><Trash2 size={13} /></button>
                            <ChevronDown size={14} className={`text-[#555] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </div>
                        </div>

                        {isExpanded && assignment.type === 'periodizacao' && (
                          <div className="border-t border-white/5 p-4 flex flex-col gap-4">
                            <p className="text-xs text-[#555] uppercase tracking-widest">Treinos da periodização</p>
                            {(assignment.treinos || []).map((treino, ti) => {
                              const treinoKey = `${assignment.id}-${ti}`;
                              const isTreinoExpanded = expandedTreino === treinoKey;
                              return (
                                <div key={ti} className="bg-[#0A0A0A] border border-white/10">
                                  <div className="flex items-center justify-between px-3 py-2 cursor-pointer" onClick={() => setExpandedTreino(isTreinoExpanded ? null : treinoKey)}>
                                    <input value={treino.name || ''} onChange={e => { e.stopPropagation(); updateAssignmentTreinoExercise(ai, ti, -1, '_name_', e.target.value); setWorkoutAssignments(prev => prev.map((a,i) => i===ai ? {...a, treinos: a.treinos.map((t,j) => j===ti ? {...t, name: e.target.value} : t)} : a)); }} onClick={e => e.stopPropagation()} placeholder={`Treino ${ti+1}`} className="bg-transparent text-sm font-medium outline-none flex-1 border-b border-white/10 pb-1 mr-2"/>
                                    <ChevronDown size={13} className={`text-[#555] transition-transform flex-shrink-0 ${isTreinoExpanded ? 'rotate-180' : ''}`} />
                                  </div>
                                  {isTreinoExpanded && (
                                    <div className="px-3 pb-3 flex flex-col gap-3 border-t border-white/5 pt-3">
                                      <div className="grid grid-cols-2 gap-2">
                                        <div>
                                          <label className="block text-xs text-[#555] mb-1">Início</label>
                                          <input type="date" value={treino.startDate || ''} onChange={e => setWorkoutAssignments(prev => prev.map((a,i) => i===ai ? {...a, treinos: a.treinos.map((t,j) => j===ti ? {...t, startDate: e.target.value} : t)} : a))} className="bg-[#121212] border border-white/10 px-2 py-1 text-sm w-full outline-none"/>
                                        </div>
                                        <div>
                                          <label className="block text-xs text-[#555] mb-1">Fim</label>
                                          <input type="date" value={treino.endDate || ''} onChange={e => setWorkoutAssignments(prev => prev.map((a,i) => i===ai ? {...a, treinos: a.treinos.map((t,j) => j===ti ? {...t, endDate: e.target.value} : t)} : a))} className="bg-[#121212] border border-white/10 px-2 py-1 text-sm w-full outline-none"/>
                                        </div>
                                      </div>
                                      <div className="overflow-x-auto">
                                        <table className="w-full text-sm border-collapse">
                                          <thead>
                                            <tr className="border-b border-white/10">
                                              <th className="text-left py-2 pr-3 text-xs text-[#555] uppercase tracking-widest font-medium">Exercício</th>
                                              <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">Séries</th>
                                              <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">Reps</th>
                                              <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">Carga</th>
                                              <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium hidden sm:table-cell">Descanso</th>
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {(treino.exercises || []).map((ex, ei) => (
                                              <tr key={ei} className="border-b border-white/5">
                                                <td className="py-1 pr-2"><input value={ex.name || ''} onChange={e => updateAssignmentTreinoExercise(ai, ti, ei, 'name', e.target.value)} placeholder="Nome" className="bg-[#0A0A0A] border border-white/10 px-2 py-1 text-sm w-full outline-none font-medium" /></td>
                                                <td className="py-1 px-1"><input value={ex.prescribedSeries || ''} onChange={e => updateAssignmentTreinoExercise(ai, ti, ei, 'prescribedSeries', e.target.value)} className="bg-[#0A0A0A] border border-white/10 px-2 py-1 text-sm w-16 outline-none text-center" /></td>
                                                <td className="py-1 px-1"><input value={ex.prescribedReps || ''} onChange={e => updateAssignmentTreinoExercise(ai, ti, ei, 'prescribedReps', e.target.value)} className="bg-[#0A0A0A] border border-white/10 px-2 py-1 text-sm w-16 outline-none text-center" /></td>
                                                <td className="py-1 px-1"><input value={ex.prescribedWeight || ''} onChange={e => updateAssignmentTreinoExercise(ai, ti, ei, 'prescribedWeight', e.target.value)} className="bg-[#0A0A0A] border border-white/10 px-2 py-1 text-sm w-20 outline-none text-center" /></td>
                                                <td className="py-1 px-1 hidden sm:table-cell"><input value={ex.rest || ''} onChange={e => updateAssignmentTreinoExercise(ai, ti, ei, 'rest', e.target.value)} className="bg-[#0A0A0A] border border-white/10 px-2 py-1 text-sm w-16 outline-none text-center" /></td>
                                              </tr>
                                            ))}
                                          </tbody>
                                        </table>
                                      </div>
                                      <button onClick={() => setWorkoutAssignments(prev => prev.map((a,i) => i===ai ? {...a, treinos: a.treinos.map((t,j) => j===ti ? {...t, exercises: [...(t.exercises||[]), {name:'',prescribedSeries:'',prescribedReps:'',prescribedWeight:'',rest:'',notes:'',actualWeight:'',actualReps:'',actualSeries:'',studentNotes:'',exerciseType:'musculação',estimatedCalories:''}]} : t)} : a))} className="flex items-center gap-1 text-xs text-[#555] hover:text-white transition-colors border border-white/5 px-3 py-1.5 self-start">
                                        <Plus size={12} /> Adicionar exercício
                                      </button>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                            <button onClick={() => setWorkoutAssignments(prev => prev.map((a,i) => i===ai ? {...a, treinos: [...(a.treinos||[]), {id: Date.now().toString(), name:'', startDate:'', endDate:'', scheduledDays:[], timeOfDay:'Manhã', exercises:[]}]} : a))} className="flex items-center gap-1 text-xs text-[#555] hover:text-white transition-colors border border-white/5 px-3 py-1.5 self-start">
                              <Plus size={12} /> Adicionar treino
                            </button>
                            <button onClick={async () => { try { await axios.put(`${BACKEND_URL}/api/admin/students/${id}/workout-assignments/${assignment.id}`, assignment, { headers }); setSavedAssignmentId(assignment.id); setTimeout(() => setSavedAssignmentId(null), 2000); } catch { setAlertMsg('Erro ao salvar periodização.'); } }}
                              className="flex items-center gap-2 px-4 py-2 bg-white text-black text-xs font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors self-start">
                              <Save size={13} /> {savedAssignmentId === assignment.id ? 'Salvo!' : 'Salvar'}
                            </button>
                          </div>
                        )}

                        {isExpanded && assignment.type !== 'periodizacao' && (
                          <div className="border-t border-white/5 p-4 flex flex-col gap-3">
                            {(!assignment.exercises || assignment.exercises.length === 0) ? (
                              <p className="text-sm text-[#555]">Nenhum exercício nesta atribuição.</p>
                            ) : (
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm border-collapse">
                                  <thead>
                                    <tr className="border-b border-white/10">
                                      <th className="text-left py-2 pr-3 text-xs text-[#555] uppercase tracking-widest font-medium">Exercício</th>
                                      <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">Séries</th>
                                      <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">Reps</th>
                                      <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">Carga</th>
                                      <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium hidden sm:table-cell">Descanso</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {assignment.exercises.map((ex, ei) => (
                                      <tr key={ei} className="border-b border-white/5">
                                        <td className="py-1 pr-2"><input value={ex.name || ''} onChange={e => updateAssignmentExercise(ai, ei, 'name', e.target.value)} placeholder="Nome" className="bg-[#0A0A0A] border border-white/10 px-2 py-1 text-sm w-full outline-none font-medium" /></td>
                                        <td className="py-1 px-1"><input value={ex.prescribedSeries || ''} onChange={e => updateAssignmentExercise(ai, ei, 'prescribedSeries', e.target.value)} className="bg-[#0A0A0A] border border-white/10 px-2 py-1 text-sm w-16 outline-none text-center" /></td>
                                        <td className="py-1 px-1"><input value={ex.prescribedReps || ''} onChange={e => updateAssignmentExercise(ai, ei, 'prescribedReps', e.target.value)} className="bg-[#0A0A0A] border border-white/10 px-2 py-1 text-sm w-16 outline-none text-center" /></td>
                                        <td className="py-1 px-1"><input value={ex.prescribedWeight || ''} onChange={e => updateAssignmentExercise(ai, ei, 'prescribedWeight', e.target.value)} className="bg-[#0A0A0A] border border-white/10 px-2 py-1 text-sm w-20 outline-none text-center" /></td>
                                        <td className="py-1 px-1 hidden sm:table-cell"><input value={ex.rest || ''} onChange={e => updateAssignmentExercise(ai, ei, 'rest', e.target.value)} className="bg-[#0A0A0A] border border-white/10 px-2 py-1 text-sm w-16 outline-none text-center" /></td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            )}
                            <button onClick={() => addExerciseToAssignment(ai)}
                              className="flex items-center gap-1 text-xs text-[#555] hover:text-white transition-colors border border-white/5 px-3 py-1.5 self-start"
                            >
                              <Plus size={12} /> Adicionar exercício
                            </button>
                            <button onClick={() => saveAssignmentExercises(assignment.id, assignment.exercises)}
                              className="flex items-center gap-2 px-4 py-2 bg-white text-black text-xs font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors self-start"
                            >
                              <Save size={13} /> {savedAssignmentId === assignment.id ? 'Salvo!' : 'Salvar'}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Workout assignment slide-over */}
        {showWorkoutPanel && (
          <div className="fixed inset-0 z-[100] flex justify-end">
            <div className="absolute inset-0 bg-black/60" onClick={() => setShowWorkoutPanel(false)} />
            <div className="relative w-full max-w-xl bg-[#0F0F0F] border-l border-white/10 h-full overflow-y-auto p-6 flex flex-col gap-5">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-bold uppercase tracking-widest">Atribuir Treino</h2>
                <button onClick={() => setShowWorkoutPanel(false)}><X size={18} /></button>
              </div>

              {/* Mode selector */}
              <div className="flex border border-white/10 overflow-hidden w-fit">
                {[['predefined','Pré-definido'],['manual','Manual']].map(([val,lbl]) => (
                  <button key={val} onClick={() => setAssignMode(val)}
                    className={`px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors ${assignMode===val ? 'bg-white text-black' : 'bg-[#121212] text-[#555] hover:text-white'}`}
                  >{lbl}</button>
                ))}
              </div>

              {/* Type selector */}
              <div className="flex border border-white/10 overflow-hidden w-fit">
                {[['treino','Treino'],['periodizacao','Periodização']].map(([val,lbl]) => (
                  <button key={val} onClick={() => setNewAssignment(a=>({...a,type:val,exercises:[],treinos:[]}))}
                    className={`px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors ${newAssignment.type===val ? 'bg-white text-black' : 'bg-[#121212] text-[#555] hover:text-white'}`}
                  >{lbl}</button>
                ))}
              </div>

              {/* Mode A: Predefined + type: treino */}
              {assignMode === 'predefined' && newAssignment.type === 'treino' && (
                <div className="flex flex-col gap-4">
                  <div>
                    <label className="block text-xs text-[#555] uppercase tracking-widest mb-1">Treino da biblioteca</label>
                    <select value={selectedTemplateId}
                      onChange={e => {
                        setSelectedTemplateId(e.target.value);
                        const tmpl = workoutTemplates.find(t => t.id === e.target.value);
                        if (tmpl) {
                          setNewAssignment(a => ({
                            ...a,
                            workoutTemplateName: tmpl.name,
                            exercises: tmpl.exercises.map(ex => ({
                              name: ex.name, prescribedSeries: ex.series, prescribedReps: ex.reps,
                              prescribedWeight: '', rest: ex.rest, notes: ex.notes,
                              actualWeight: '', actualReps: '', actualSeries: '', studentNotes: '',
                              exerciseType: ex.exerciseType || 'musculação', estimatedCalories: ex.estimatedCalories || '',
                            }))
                          }));
                        }
                      }}
                      className="bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm w-full outline-none"
                    >
                      <option value="">Selecione um treino...</option>
                      {workoutTemplates.map(t => <option key={t.id} value={t.id}>{t.name} {t.category ? `(${t.category})` : ''}</option>)}
                    </select>
                  </div>
                  {newAssignment.exercises.length > 0 && (
                    <div className="flex flex-col gap-2">
                      <label className="block text-xs text-[#555] uppercase tracking-widest">Exercícios e cargas</label>
                      {newAssignment.exercises.map((ex, ei) => (
                        <div key={ei} className="bg-[#0A0A0A] border border-white/5 p-3 flex flex-col gap-2">
                          <div className="flex items-center justify-between">
                            <input value={ex.name} onChange={e => setNewAssignment(a=>({...a,exercises:a.exercises.map((x,i)=>i===ei?{...x,name:e.target.value}:x)}))} placeholder="Nome do exercício" className="bg-transparent text-sm font-medium outline-none flex-1 border-b border-white/10 pb-1"/>
                            <button onClick={() => setNewAssignment(a => ({...a, exercises: a.exercises.filter((_,i)=>i!==ei)}))} className="text-[#555] hover:text-red-400 ml-2"><X size={13}/></button>
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <div><label className="block text-xs text-[#555] mb-1">Séries</label><input value={ex.prescribedSeries} onChange={e => setNewAssignment(a=>({...a,exercises:a.exercises.map((x,i)=>i===ei?{...x,prescribedSeries:e.target.value}:x)}))} className="bg-[#121212] border border-white/10 px-2 py-1 text-sm w-full outline-none"/></div>
                            <div><label className="block text-xs text-[#555] mb-1">Reps</label><input value={ex.prescribedReps} onChange={e => setNewAssignment(a=>({...a,exercises:a.exercises.map((x,i)=>i===ei?{...x,prescribedReps:e.target.value}:x)}))} className="bg-[#121212] border border-white/10 px-2 py-1 text-sm w-full outline-none"/></div>
                            <div><label className="block text-xs text-[#555] mb-1">Carga</label><input value={ex.prescribedWeight} onChange={e => setNewAssignment(a=>({...a,exercises:a.exercises.map((x,i)=>i===ei?{...x,prescribedWeight:e.target.value}:x)}))} className="bg-[#121212] border border-white/10 px-2 py-1 text-sm w-full outline-none"/></div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="block text-xs text-[#555] mb-1">Tipo</label>
                              <input
                                list={`exercise-types-pre-${ei}`}
                                value={ex.exerciseType || 'musculação'}
                                onChange={e => setNewAssignment(a=>({...a,exercises:a.exercises.map((x,i)=>i===ei?{...x,exerciseType:e.target.value}:x)}))}
                                className="bg-[#121212] border border-white/10 px-2 py-1 text-sm w-full outline-none"
                                placeholder="musculação"
                              />
                              <datalist id={`exercise-types-pre-${ei}`}>
                                {['Musculação','Cardio','Natação','Funcional','Luta','Esteira','Corrida','Outro'].map(t => <option key={t} value={t} />)}
                              </datalist>
                            </div>
                            <div>
                              <label className="block text-xs text-[#555] mb-1">Gasto kcal (sessão)</label>
                              <input
                                type="number"
                                value={ex.estimatedCalories || ''}
                                onChange={e => setNewAssignment(a=>({...a,exercises:a.exercises.map((x,i)=>i===ei?{...x,estimatedCalories:e.target.value}:x)}))}
                                className="bg-[#121212] border border-white/10 px-2 py-1 text-sm w-full outline-none"
                                placeholder="300"
                              />
                            </div>
                          </div>
                        </div>
                      ))}
                      <button onClick={() => setNewAssignment(a=>({...a,exercises:[...a.exercises,{name:'',prescribedSeries:'',prescribedReps:'',prescribedWeight:'',rest:'',notes:'',actualWeight:'',actualReps:'',actualSeries:'',studentNotes:'',exerciseType:'musculação',estimatedCalories:''}]}))}
                        className="flex items-center gap-1 text-xs text-[#555] hover:text-white border border-white/5 px-3 py-1.5 self-start transition-colors">
                        <Plus size={12}/> Adicionar exercício
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Mode A: Predefined + type: periodizacao */}
              {assignMode === 'predefined' && newAssignment.type === 'periodizacao' && (
                <div className="flex flex-col gap-4">
                  <div>
                    <label className="block text-xs text-[#555] uppercase tracking-widest mb-1">Periodização da biblioteca</label>
                    <select value={selectedPeriodizacaoId}
                      onChange={e => {
                        setSelectedPeriodizacaoId(e.target.value);
                        const prog = workoutPrograms.find(p => p.id === e.target.value);
                        if (prog) setNewAssignment(a => ({...a, name: prog.name}));
                      }}
                      className="bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm w-full outline-none"
                    >
                      <option value="">Selecione uma periodização...</option>
                      {workoutPrograms.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </div>
                  {selectedPeriodizacaoId && (() => {
                    const prog = workoutPrograms.find(p => p.id === selectedPeriodizacaoId);
                    return prog ? (
                      <div className="bg-[#0A0A0A] border border-white/5 p-3">
                        <p className="text-xs text-[#555] mb-2 uppercase tracking-widest">Treinos incluídos</p>
                        {(prog.days || []).map((day, i) => {
                          const tmpl = workoutTemplates.find(t => t.id === day.workoutTemplateId);
                          return (
                            <div key={i} className="flex items-center gap-3 py-1.5 border-b border-white/5 last:border-0">
                              <span className="text-xs border border-white/10 px-2 py-0.5 text-[#A3A3A3] min-w-[70px] text-center">{day.weekday}</span>
                              <span className="text-sm flex-1">{tmpl ? tmpl.name : day.workoutTemplateName || <span className="text-[#555]">Treino</span>}</span>
                              <span className="text-xs text-[#555]">{day.timeOfDay}</span>
                            </div>
                          );
                        })}
                      </div>
                    ) : null;
                  })()}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs text-[#555] uppercase tracking-widest mb-1">Início (global)</label>
                      <input type="date" value={newAssignment.startDate} onChange={e=>setNewAssignment(a=>({...a,startDate:e.target.value}))} className="bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm w-full outline-none"/>
                    </div>
                    <div>
                      <label className="block text-xs text-[#555] uppercase tracking-widest mb-1">Fim (global)</label>
                      <input type="date" value={newAssignment.endDate} onChange={e=>setNewAssignment(a=>({...a,endDate:e.target.value}))} className="bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm w-full outline-none"/>
                    </div>
                  </div>
                </div>
              )}

              {/* Mode B: Manual + type: treino */}
              {assignMode === 'manual' && newAssignment.type === 'treino' && (
                <div className="flex flex-col gap-4">
                  <div>
                    <label className="block text-xs text-[#555] uppercase tracking-widest mb-1">Nome do treino</label>
                    <input value={newAssignment.workoutTemplateName} onChange={e => setNewAssignment(a=>({...a,workoutTemplateName:e.target.value}))} className="bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm w-full outline-none" placeholder="Ex: Treino A - Peito"/>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="block text-xs text-[#555] uppercase tracking-widest">Exercícios</label>
                    {newAssignment.exercises.map((ex, ei) => (
                      <div key={ei} className="bg-[#0A0A0A] border border-white/5 p-3 flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <input value={ex.name} onChange={e => setNewAssignment(a=>({...a,exercises:a.exercises.map((x,i)=>i===ei?{...x,name:e.target.value}:x)}))} placeholder="Nome do exercício" className="bg-transparent text-sm font-medium outline-none flex-1 border-b border-white/10 pb-1"/>
                          <button onClick={() => setNewAssignment(a=>({...a,exercises:a.exercises.filter((_,i)=>i!==ei)}))} className="text-[#555] hover:text-red-400 ml-2"><X size={13}/></button>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <div><label className="block text-xs text-[#555] mb-1">Séries</label><input value={ex.prescribedSeries} onChange={e => setNewAssignment(a=>({...a,exercises:a.exercises.map((x,i)=>i===ei?{...x,prescribedSeries:e.target.value}:x)}))} className="bg-[#121212] border border-white/10 px-2 py-1 text-sm w-full outline-none"/></div>
                          <div><label className="block text-xs text-[#555] mb-1">Reps</label><input value={ex.prescribedReps} onChange={e => setNewAssignment(a=>({...a,exercises:a.exercises.map((x,i)=>i===ei?{...x,prescribedReps:e.target.value}:x)}))} className="bg-[#121212] border border-white/10 px-2 py-1 text-sm w-full outline-none"/></div>
                          <div><label className="block text-xs text-[#555] mb-1">Carga</label><input value={ex.prescribedWeight} onChange={e => setNewAssignment(a=>({...a,exercises:a.exercises.map((x,i)=>i===ei?{...x,prescribedWeight:e.target.value}:x)}))} className="bg-[#121212] border border-white/10 px-2 py-1 text-sm w-full outline-none"/></div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs text-[#555] mb-1">Tipo</label>
                            <input
                              list={`exercise-types-man-${ei}`}
                              value={ex.exerciseType || 'musculação'}
                              onChange={e => setNewAssignment(a=>({...a,exercises:a.exercises.map((x,i)=>i===ei?{...x,exerciseType:e.target.value}:x)}))}
                              className="bg-[#121212] border border-white/10 px-2 py-1 text-sm w-full outline-none"
                              placeholder="musculação"
                            />
                            <datalist id={`exercise-types-man-${ei}`}>
                              {['Musculação','Cardio','Natação','Funcional','Luta','Esteira','Corrida','Outro'].map(t => <option key={t} value={t} />)}
                            </datalist>
                          </div>
                          <div>
                            <label className="block text-xs text-[#555] mb-1">Gasto kcal (sessão)</label>
                            <input
                              type="number"
                              value={ex.estimatedCalories || ''}
                              onChange={e => setNewAssignment(a=>({...a,exercises:a.exercises.map((x,i)=>i===ei?{...x,estimatedCalories:e.target.value}:x)}))}
                              className="bg-[#121212] border border-white/10 px-2 py-1 text-sm w-full outline-none"
                              placeholder="300"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                    <button onClick={() => setNewAssignment(a=>({...a,exercises:[...a.exercises,{name:'',prescribedSeries:'',prescribedReps:'',prescribedWeight:'',rest:'',notes:'',actualWeight:'',actualReps:'',actualSeries:'',studentNotes:'',exerciseType:'musculação',estimatedCalories:''}]}))}
                      className="flex items-center gap-1 text-xs text-[#555] hover:text-white border border-white/5 px-3 py-1.5 self-start transition-colors">
                      <Plus size={12}/> Adicionar exercício
                    </button>
                  </div>
                </div>
              )}

              {/* Mode B: Manual + type: periodizacao */}
              {assignMode === 'manual' && newAssignment.type === 'periodizacao' && (
                <div className="flex flex-col gap-4">
                  <div>
                    <label className="block text-xs text-[#555] uppercase tracking-widest mb-1">Nome da periodização</label>
                    <input value={newAssignment.name} onChange={e => setNewAssignment(a=>({...a,name:e.target.value}))} className="bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm w-full outline-none" placeholder="Ex: Periodização Hipertrofia 12 semanas"/>
                  </div>
                  <div className="flex flex-col gap-3">
                    <div className="flex items-center justify-between">
                      <label className="block text-xs text-[#555] uppercase tracking-widest">Treinos</label>
                      <button onClick={() => setNewAssignment(a=>({...a,treinos:[...a.treinos,emptyTreinoItem()]}))}
                        className="flex items-center gap-1 text-xs text-[#A3A3A3] hover:text-white border border-white/10 px-2 py-1 transition-colors">
                        <Plus size={11}/> Treino
                      </button>
                    </div>
                    {newAssignment.treinos.map((treino, ti) => (
                      <div key={ti} className="bg-[#0A0A0A] border border-white/5 p-3 flex flex-col gap-3">
                        <div className="flex items-center justify-between">
                          <input value={treino.name} onChange={e => updateTreinoField(ti,'name',e.target.value)} placeholder={`Treino ${ti+1}`} className="bg-transparent text-sm font-medium outline-none flex-1 border-b border-white/10 pb-1"/>
                          <button onClick={() => setNewAssignment(a=>({...a,treinos:a.treinos.filter((_,i)=>i!==ti)}))} className="text-[#555] hover:text-red-400 ml-2"><X size={13}/></button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <label className="block text-xs text-[#555] mb-1">Início</label>
                            <input type="date" value={treino.startDate} onChange={e => updateTreinoField(ti,'startDate',e.target.value)} className="bg-[#121212] border border-white/10 px-2 py-1 text-sm w-full outline-none"/>
                          </div>
                          <div>
                            <label className="block text-xs text-[#555] mb-1">Fim</label>
                            <input type="date" value={treino.endDate} onChange={e => updateTreinoField(ti,'endDate',e.target.value)} className="bg-[#121212] border border-white/10 px-2 py-1 text-sm w-full outline-none"/>
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs text-[#555] mb-2">Dias</label>
                          <div className="flex flex-wrap gap-1">
                            {['Segunda','Terça','Quarta','Quinta','Sexta','Sábado','Domingo'].map(d => (
                              <button key={d} type="button"
                                onClick={() => updateTreinoField(ti,'scheduledDays',treino.scheduledDays.includes(d)?treino.scheduledDays.filter(x=>x!==d):[...treino.scheduledDays,d])}
                                className={`px-2 py-1 text-xs border transition-colors ${treino.scheduledDays.includes(d)?'border-white text-white bg-white/10':'border-white/10 text-[#555] hover:text-white'}`}
                              >{d}</button>
                            ))}
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <div className="flex items-center justify-between">
                            <label className="block text-xs text-[#555] uppercase tracking-widest">Exercícios</label>
                            <button onClick={() => updateTreinoField(ti,'exercises',[...treino.exercises,emptyExerciseItem()])}
                              className="flex items-center gap-1 text-xs text-[#555] hover:text-white border border-white/5 px-2 py-0.5 transition-colors">
                              <Plus size={11}/> Exercício
                            </button>
                          </div>
                          {treino.exercises.map((ex, ei) => (
                            <div key={ei} className="bg-[#121212] border border-white/5 p-2 flex flex-col gap-2">
                              <div className="flex items-center justify-between">
                                <input value={ex.name} onChange={e => updateTreinoExercise(ti,ei,'name',e.target.value)} placeholder="Nome" className="bg-transparent text-sm outline-none flex-1 border-b border-white/10 pb-1"/>
                                <button onClick={() => updateTreinoField(ti,'exercises',treino.exercises.filter((_,i)=>i!==ei))} className="text-[#555] hover:text-red-400 ml-2"><X size={11}/></button>
                              </div>
                              <div className="grid grid-cols-3 gap-1">
                                <div><label className="block text-xs text-[#555] mb-0.5">Séries</label><input value={ex.prescribedSeries} onChange={e => updateTreinoExercise(ti,ei,'prescribedSeries',e.target.value)} className="bg-[#0A0A0A] border border-white/10 px-2 py-1 text-xs w-full outline-none"/></div>
                                <div><label className="block text-xs text-[#555] mb-0.5">Reps</label><input value={ex.prescribedReps} onChange={e => updateTreinoExercise(ti,ei,'prescribedReps',e.target.value)} className="bg-[#0A0A0A] border border-white/10 px-2 py-1 text-xs w-full outline-none"/></div>
                                <div><label className="block text-xs text-[#555] mb-0.5">Carga</label><input value={ex.prescribedWeight} onChange={e => updateTreinoExercise(ti,ei,'prescribedWeight',e.target.value)} className="bg-[#0A0A0A] border border-white/10 px-2 py-1 text-xs w-full outline-none"/></div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                    {newAssignment.treinos.length === 0 && (
                      <p className="text-xs text-[#555] py-2">Nenhum treino adicionado. Clique em "Treino" para adicionar.</p>
                    )}
                  </div>
                </div>
              )}

              {/* Common: scheduled days + time + dates (only for treino type) */}
              {newAssignment.type === 'treino' && (
                <>
                  <div>
                    <label className="block text-xs text-[#555] uppercase tracking-widest mb-2">Dias da semana</label>
                    <div className="flex flex-wrap gap-2">
                      {['Segunda','Terça','Quarta','Quinta','Sexta','Sábado','Domingo'].map(d => (
                        <button key={d} type="button"
                          onClick={() => setNewAssignment(a=>({...a,scheduledDays:a.scheduledDays.includes(d)?a.scheduledDays.filter(x=>x!==d):[...a.scheduledDays,d]}))}
                          className={`px-3 py-1.5 text-xs border transition-colors ${newAssignment.scheduledDays.includes(d)?'border-white text-white bg-white/10':'border-white/10 text-[#555] hover:text-white'}`}
                        >{d}</button>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs text-[#555] uppercase tracking-widest mb-1">Turno</label>
                      <select value={newAssignment.timeOfDay} onChange={e=>setNewAssignment(a=>({...a,timeOfDay:e.target.value}))} className="bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm w-full outline-none">
                        {['Manhã','Tarde','Noite'].map(t=><option key={t}>{t}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs text-[#555] uppercase tracking-widest mb-1">Início</label>
                      <input type="date" value={newAssignment.startDate} onChange={e=>setNewAssignment(a=>({...a,startDate:e.target.value}))} className="bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm w-full outline-none"/>
                    </div>
                    <div>
                      <label className="block text-xs text-[#555] uppercase tracking-widest mb-1">Fim</label>
                      <input type="date" value={newAssignment.endDate} onChange={e=>setNewAssignment(a=>({...a,endDate:e.target.value}))} className="bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm w-full outline-none"/>
                    </div>
                  </div>
                </>
              )}

              <button
                onClick={async () => {
                  if (newAssignment.type === 'periodizacao') {
                    if (assignMode === 'manual' && newAssignment.treinos.length === 0) { setAlertMsg('Adicione pelo menos um treino na periodização.'); return; }
                    if (assignMode === 'predefined' && !selectedPeriodizacaoId) { setAlertMsg('Selecione uma periodização da biblioteca.'); return; }
                    try {
                      const res = await axios.post(`${BACKEND_URL}/api/admin/students/${id}/workout-assignments`, {
                        ...newAssignment,
                        workoutProgramId: assignMode === 'predefined' ? selectedPeriodizacaoId : null,
                        startDate: newAssignment.treinos[0]?.startDate || newAssignment.startDate,
                        endDate: newAssignment.treinos[newAssignment.treinos.length-1]?.endDate || newAssignment.endDate,
                      }, { headers });
                      setWorkoutAssignments(prev => [res.data, ...prev]);
                      setShowWorkoutPanel(false);
                      setNewAssignment({ type:'treino', name:'', workoutTemplateName:'', startDate:'', endDate:'', scheduledDays:[], timeOfDay:'Manhã', exercises:[], treinos:[] });
                      setSelectedPeriodizacaoId('');
                    } catch (err) { setAlertMsg(err.response?.data?.detail || 'Erro ao atribuir periodização.'); }
                    return;
                  }
                  // treino simples
                  if (!newAssignment.startDate || !newAssignment.endDate) { setAlertMsg('Defina as datas de início e fim.'); return; }
                  if (newAssignment.exercises.length === 0) { setAlertMsg('Adicione pelo menos um exercício.'); return; }
                  try {
                    const res = await axios.post(`${BACKEND_URL}/api/admin/students/${id}/workout-assignments`, {
                      ...newAssignment,
                      workoutTemplateId: assignMode === 'predefined' ? selectedTemplateId : null,
                    }, { headers });
                    const arr = Array.isArray(res.data) ? res.data : [res.data];
                    setWorkoutAssignments(prev => [...arr, ...prev]);
                    setShowWorkoutPanel(false);
                    setNewAssignment({ type:'treino', name:'', workoutTemplateName:'', startDate:'', endDate:'', scheduledDays:[], timeOfDay:'Manhã', exercises:[], treinos:[] });
                    setSelectedTemplateId('');
                  } catch (err) { setAlertMsg(err.response?.data?.detail || 'Erro ao atribuir treino.'); }
                }}
                className="w-full py-3 bg-white text-black font-bold uppercase tracking-widest text-sm hover:bg-[#E5E5E5] transition-colors"
              >
                {newAssignment.type === 'periodizacao' ? 'Atribuir periodização' : 'Atribuir treino'}
              </button>
            </div>
          </div>
        )}

        {/* ── DIETA ── */}
        {tab === 'diet' && (
          <div className="flex flex-col gap-6">
            {/* Diet assignments section */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xs font-bold uppercase tracking-widest text-[#A3A3A3]">Atribuições de dieta</h2>
                <div className="flex items-center gap-2">
                  {dietAssignments.length > 0 && (
                    <button onClick={exportDietPDF}
                      className="flex items-center gap-2 px-4 py-2 bg-[#121212] border border-white/10 text-xs font-bold uppercase tracking-wider hover:border-white/30 transition-colors">
                      <FileDown size={13} /> Exportar PDF
                    </button>
                  )}
                  <button onClick={() => { setEditingAssignment(null); setDietAssignForm({ name: '', templateId: '', startDate: '', endDate: '', meals: [] }); setShowDietPanel(true); }}
                    className="flex items-center gap-2 px-4 py-2 bg-white text-black text-xs font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors">
                    <Plus size={13} /> Atribuir dieta
                  </button>
                </div>
              </div>

              {dietAssignments.length === 0 ? (
                <div className="text-center py-8 text-[#555] text-sm bg-[#121212] border border-white/5">
                  Nenhuma atribuição de dieta ainda.
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  {dietAssignments.map((assignment, ai) => {
                    const today = new Date().toISOString().slice(0, 10);
                    let badgeCls = 'text-green-400 border-green-500/30';
                    let badgeLabel = 'Ativo';
                    if (assignment.endDate && assignment.endDate < today) { badgeCls = 'text-[#555] border-white/10'; badgeLabel = 'Encerrado'; }
                    else if (assignment.startDate && assignment.startDate > today) { badgeCls = 'text-yellow-400 border-yellow-400/30'; badgeLabel = 'Próximo'; }
                    const isExpanded = expandedDietAssignment === assignment.id;
                    return (
                      <div key={assignment.id} className="bg-[#121212] border border-white/5">
                        {/* Header row */}
                        <div
                          className="px-5 py-4 flex items-center justify-between gap-3 cursor-pointer"
                          onClick={() => setExpandedDietAssignment(isExpanded ? null : assignment.id)}
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-sm">{assignment.name || 'Dieta'}</span>
                              <span className={`text-xs border px-2 py-0.5 ${badgeCls}`}>{badgeLabel}</span>
                            </div>
                            <p className="text-xs text-[#555] mt-0.5">
                              {assignment.startDate || '?'} — {assignment.endDate || 'sem prazo'}
                              {assignment.meals?.length > 0 && ` · ${assignment.meals.length} refeição(ões)`}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            <button onClick={e => { e.stopPropagation(); setConfirm({ message: 'Remover esta atribuição? Esta ação não pode ser desfeita.', onConfirm: async () => { try { await axios.delete(`${BACKEND_URL}/api/admin/students/${id}/diet-assignments/${assignment.id}`, { headers }); setDietAssignments(prev => prev.filter(a => a.id !== assignment.id)); } catch { setAlertMsg('Erro ao remover.'); } } }); }} className="text-[#555] hover:text-red-400 transition-colors"><Trash2 size={13} /></button>
                            <ChevronDown size={14} className={`text-[#555] transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                          </div>
                        </div>

                        {/* Expanded inline editor */}
                        {isExpanded && (
                          <div className="border-t border-white/5 px-5 pb-5 pt-4 flex flex-col gap-4">
                            {/* Name + dates */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                              <div className="sm:col-span-1">
                                <label className="block text-xs text-[#555] mb-1">Nome</label>
                                <input value={assignment.name || ''} onChange={e => setDietAssignments(prev => prev.map((a, i) => i === ai ? { ...a, name: e.target.value } : a))} className="bg-[#0A0A0A] border border-white/10 px-2 py-1.5 text-sm w-full outline-none" />
                              </div>
                              <div>
                                <label className="block text-xs text-[#555] mb-1">Início</label>
                                <input type="date" value={assignment.startDate || ''} onChange={e => setDietAssignments(prev => prev.map((a, i) => i === ai ? { ...a, startDate: e.target.value } : a))} className="bg-[#0A0A0A] border border-white/10 px-2 py-1.5 text-sm w-full outline-none" />
                              </div>
                              <div>
                                <label className="block text-xs text-[#555] mb-1">Fim</label>
                                <input type="date" value={assignment.endDate || ''} onChange={e => setDietAssignments(prev => prev.map((a, i) => i === ai ? { ...a, endDate: e.target.value } : a))} className="bg-[#0A0A0A] border border-white/10 px-2 py-1.5 text-sm w-full outline-none" />
                              </div>
                            </div>

                            {/* Meals */}
                            <div className="flex flex-col gap-3">
                              {(assignment.meals || []).map((meal, mi) => (
                                <div key={mi} className="bg-[#0A0A0A] border border-white/10">
                                  {/* Meal header */}
                                  <div className="flex items-center gap-2 px-3 py-2 border-b border-white/5">
                                    <input value={meal.name || ''} onChange={e => updateDietAssignmentMeal(ai, mi, 'name', e.target.value)} placeholder={`Refeição ${mi + 1}`} className="bg-transparent text-sm font-medium outline-none flex-1 min-w-0" />
                                    <div className="flex items-center gap-1 flex-shrink-0">
                                      <span className="text-xs text-[#555]">Horário:</span>
                                      <input type="time" value={meal.time || ''} onChange={e => updateDietAssignmentMeal(ai, mi, 'time', e.target.value)} className="bg-[#121212] border border-white/10 px-1.5 py-0.5 text-xs outline-none w-24" />
                                    </div>
                                    <button onClick={() => removeDietAssignmentMeal(ai, mi)} className="text-[#555] hover:text-red-400 transition-colors flex-shrink-0"><X size={13} /></button>
                                  </div>
                                  {/* Days selector */}
                                  <div className="flex items-center gap-1.5 px-3 py-2 border-b border-white/5 flex-wrap">
                                    <span className="text-xs text-[#555] mr-1">Dias:</span>
                                    {ALL_DAYS.map(d => {
                                      const short = d.slice(0, 3);
                                      const active = (meal.days || ALL_DAYS).includes(d);
                                      return (
                                        <button key={d} type="button" onClick={() => {
                                          const cur = meal.days || ALL_DAYS;
                                          updateDietAssignmentMeal(ai, mi, 'days', active ? cur.filter(x => x !== d) : [...cur, d]);
                                        }} className={`px-1.5 py-0.5 text-xs border transition-colors ${active ? 'border-white/40 text-white' : 'border-white/10 text-[#444] hover:text-[#888]'}`}>
                                          {short}
                                        </button>
                                      );
                                    })}
                                    <span className="text-xs text-[#444] ml-1">{(meal.days || ALL_DAYS).length}×/sem</span>
                                  </div>
                                  {/* Foods table */}
                                  <div className="px-3 pb-3 pt-2 flex flex-col gap-1">
                                    <div className="grid grid-cols-7 gap-1 mb-1">
                                      {['Alimento', 'Qtd', 'Estado', 'Kcal', 'Prot', 'Carb', 'Gord'].map(h => (
                                        <span key={h} className="text-xs text-[#555] uppercase tracking-wider">{h}</span>
                                      ))}
                                    </div>
                                    {(meal.foods || []).map((food, fi) => (
                                      <div key={fi} className="grid grid-cols-7 gap-1 items-center">
                                        <input value={food.name || ''} onChange={e => updateDietAssignmentFood(ai, mi, fi, 'name', e.target.value)} placeholder="Nome" className="bg-[#121212] border border-white/10 px-2 py-1 text-xs outline-none" />
                                        <input value={food.quantity || ''} onChange={e => updateDietAssignmentFood(ai, mi, fi, 'quantity', e.target.value)} placeholder="100g" className="bg-[#121212] border border-white/10 px-2 py-1 text-xs outline-none" />
                                        <select value={food.preparation || 'cru'} onChange={e => updateDietAssignmentFood(ai, mi, fi, 'preparation', e.target.value)} className="bg-[#121212] border border-white/10 px-1 py-1 text-xs outline-none">
                                          <option value="cru">Cru</option>
                                          <option value="cozido">Cozido</option>
                                          <option value="outro">Outro</option>
                                        </select>
                                        <input value={food.calories || ''} onChange={e => updateDietAssignmentFood(ai, mi, fi, 'calories', e.target.value)} placeholder="0" type="number" className="bg-[#121212] border border-white/10 px-2 py-1 text-xs outline-none" />
                                        <input value={food.protein || ''} onChange={e => updateDietAssignmentFood(ai, mi, fi, 'protein', e.target.value)} placeholder="0" type="number" className="bg-[#121212] border border-white/10 px-2 py-1 text-xs outline-none" />
                                        <input value={food.carbs || ''} onChange={e => updateDietAssignmentFood(ai, mi, fi, 'carbs', e.target.value)} placeholder="0" type="number" className="bg-[#121212] border border-white/10 px-2 py-1 text-xs outline-none" />
                                        <div className="flex items-center gap-1">
                                          <input value={food.fat || ''} onChange={e => updateDietAssignmentFood(ai, mi, fi, 'fat', e.target.value)} placeholder="0" type="number" className="bg-[#121212] border border-white/10 px-2 py-1 text-xs outline-none flex-1 min-w-0" />
                                          <button onClick={() => removeDietAssignmentFood(ai, mi, fi)} className="text-[#555] hover:text-red-400 flex-shrink-0"><X size={11} /></button>
                                        </div>
                                      </div>
                                    ))}
                                    <button onClick={() => addDietAssignmentFood(ai, mi)} className="flex items-center gap-1 text-xs text-[#555] hover:text-white transition-colors border border-white/5 px-2 py-1 self-start mt-1">
                                      <Plus size={11} /> Adicionar alimento
                                    </button>
                                  </div>
                                </div>
                              ))}
                              <button onClick={() => addDietAssignmentMeal(ai)} className="flex items-center gap-1 text-xs text-[#555] hover:text-white transition-colors border border-white/5 px-3 py-1.5 self-start">
                                <Plus size={12} /> Adicionar refeição
                              </button>
                            </div>

                            {/* Notes */}
                            <div>
                              <label className="block text-xs text-[#555] mb-1">Observações</label>
                              <textarea value={assignment.notes || ''} onChange={e => setDietAssignments(prev => prev.map((a, i) => i === ai ? { ...a, notes: e.target.value } : a))} rows={2} className="bg-[#0A0A0A] border border-white/10 px-2 py-1.5 text-sm w-full outline-none resize-none" />
                            </div>

                            <button onClick={() => saveDietAssignment(assignment.id, assignment)} className="flex items-center gap-2 px-4 py-2 bg-white text-black text-xs font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors self-start">
                              <Save size={13} /> {savedDietAssignmentId === assignment.id ? 'Salvo!' : 'Salvar'}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Shopping list — computed from diet assignments in the frontend */}
            {(() => {
              const fmtKg = (g) => g >= 1000 ? `${(g / 1000).toFixed(2).replace('.', ',')}kg` : `${Math.round(g)}g`;
              const foodMap = {};
              for (const a of dietAssignments) {
                if (!a.active) continue;
                for (const meal of (a.meals || [])) {
                  const daysPerWeek = (meal.days && meal.days.length > 0) ? meal.days.length : 7;
                  for (const food of (meal.foods || [])) {
                    if (!food.name?.trim()) continue;
                    const prep = food.preparation || 'cru';
                    const key = `${food.name.toLowerCase().trim()}__${prep}`;
                    const qty = parseFloat(food.quantity) || 0;
                    if (!foodMap[key]) foodMap[key] = { name: food.name, prep, weeklyG: 0, hasQty: false };
                    if (qty > 0) foodMap[key].hasQty = true;
                    foodMap[key].weeklyG += qty * daysPerWeek;
                  }
                }
              }
              const items = Object.values(foodMap);
              return (
                <div className="bg-[#121212] border border-white/5 p-5">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xs font-bold uppercase tracking-widest text-[#A3A3A3] flex items-center gap-2">
                      <ShoppingCart size={13} /> Lista de compras
                    </h3>
                    <span className="text-xs text-[#555]">Calculado automaticamente pela dieta</span>
                  </div>
                  {items.length === 0 ? (
                    <p className="text-sm text-[#555]">Nenhum alimento encontrado. Adicione alimentos nas refeições da dieta atribuída e salve.</p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {/* Disclaimer */}
                      <div className="bg-[#0A0A0A] border border-white/5 px-3 py-2 text-xs text-[#555] leading-relaxed">
                        Pesos calculados com base nos dias ativos de cada refeição. <strong className="text-[#777]">Atenção:</strong> alimentos marcados como <span className="text-yellow-400/80">cozido</span> foram prescritos no estado cozido — converta para o equivalente cru na hora da compra (ex: 1kg cozido ≈ 1,6kg cru para carnes).
                      </div>
                      {items.some(it => !it.hasQty) && (
                        <p className="text-xs text-yellow-400/80">Alguns alimentos não possuem quantidade definida nas refeições.</p>
                      )}
                      {/* Header */}
                      <div className="grid grid-cols-4 gap-2 px-1 mt-1">
                        <span className="col-span-2 text-xs text-[#555] uppercase tracking-wider">Alimento</span>
                        <span className="text-xs text-[#555] uppercase tracking-wider text-right">Semanal</span>
                        <span className="text-xs text-[#555] uppercase tracking-wider text-right">Mensal</span>
                      </div>
                      {items.map((item, i) => (
                        <div key={i} className="grid grid-cols-4 gap-2 items-center py-1.5 border-b border-white/5">
                          <div className="col-span-2 flex items-center gap-2 min-w-0">
                            <span className={`text-sm truncate ${item.hasQty ? 'text-[#E5E5E5]' : 'text-[#777]'}`}>{item.name}</span>
                            {item.prep !== 'cru' && (
                              <span className={`text-xs px-1.5 py-0.5 border flex-shrink-0 ${item.prep === 'cozido' ? 'text-yellow-400/80 border-yellow-400/20' : 'text-[#555] border-white/10'}`}>{item.prep}</span>
                            )}
                          </div>
                          <span className="text-sm text-right text-[#E5E5E5]">{item.hasQty ? fmtKg(item.weeklyG) : '—'}</span>
                          <span className="text-sm text-right text-[#A3A3A3]">{item.hasQty ? fmtKg(item.weeklyG * 4.3) : '—'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            <hr className="border-white/5" />

            {/* Legacy static diet editor */}
            <details>
              <summary className="text-xs font-bold uppercase tracking-widest text-[#555] cursor-pointer hover:text-[#A3A3A3] transition-colors">
                Editor de dieta legado (clique para expandir)
              </summary>
              <div className="flex flex-col gap-6 mt-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className={labelCls}>Nome da dieta</label>
                    <input className={inputCls} value={diet.name} onChange={e => setDiet(d => ({ ...d, name: e.target.value }))} />
                  </div>
                  <div>
                    <label className={labelCls}>Meta calórica (kcal)</label>
                    <input className={inputCls} type="number" placeholder="2000" value={diet.calories} onChange={e => setDiet(d => ({ ...d, calories: e.target.value }))} />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className={labelCls}>Refeições</label>
                    <button onClick={addMeal} className="flex items-center gap-1 text-xs text-[#A3A3A3] hover:text-white border border-white/10 px-2 py-1 transition-colors">
                      <Plus size={12} /> Adicionar refeição
                    </button>
                  </div>

                  <div className="flex flex-col gap-4">
                    {diet.meals.map((meal, mi) => (
                      <div key={mi} className="bg-[#121212] border border-white/5 p-4">
                        <div className="flex items-center justify-between mb-3">
                          <span className="text-xs text-[#555] uppercase tracking-widest">Refeição {mi + 1}</span>
                          {diet.meals.length > 1 && (
                            <button onClick={() => removeMeal(mi)} className="text-[#555] hover:text-red-400 transition-colors"><Trash2 size={13} /></button>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          <div>
                            <label className={labelCls}>Nome</label>
                            <input className={inputCls} placeholder="Café da manhã" value={meal.name} onChange={e => setMeal(mi, 'name', e.target.value)} />
                          </div>
                          <div>
                            <label className={labelCls}>Horário</label>
                            <input className={inputCls} placeholder="07:00" value={meal.time} onChange={e => setMeal(mi, 'time', e.target.value)} />
                          </div>
                        </div>

                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-[#555] uppercase tracking-widest">Alimentos</span>
                          <button onClick={() => addFood(mi)} className="flex items-center gap-1 text-xs text-[#555] hover:text-white border border-white/5 px-2 py-1 transition-colors">
                            <Plus size={11} /> Alimento
                          </button>
                        </div>

                        <div className="flex flex-col gap-2">
                          {meal.foods.map((food, fi) => (
                            <div key={fi} className="grid grid-cols-3 sm:grid-cols-6 gap-2 items-end">
                              <div className="col-span-2 sm:col-span-2">
                                <label className={fi === 0 ? labelCls : 'hidden'}>Alimento</label>
                                <input className={inputCls} placeholder="Frango grelhado" value={food.name} onChange={e => setFood(mi, fi, 'name', e.target.value)} />
                              </div>
                              <div>
                                <label className={fi === 0 ? labelCls : 'hidden'}>Qtd</label>
                                <input className={inputCls} placeholder="100g" value={food.quantity} onChange={e => setFood(mi, fi, 'quantity', e.target.value)} />
                              </div>
                              <div>
                                <label className={fi === 0 ? labelCls : 'hidden'}>Kcal</label>
                                <input className={inputCls} type="number" placeholder="165" value={food.calories} onChange={e => setFood(mi, fi, 'calories', e.target.value)} />
                              </div>
                              <div>
                                <label className={fi === 0 ? labelCls : 'hidden'}>P (g)</label>
                                <input className={inputCls} type="number" placeholder="31" value={food.protein} onChange={e => setFood(mi, fi, 'protein', e.target.value)} />
                              </div>
                              <div className="flex gap-1">
                                <div className="flex-1">
                                  <label className={fi === 0 ? labelCls : 'hidden'}>C (g)</label>
                                  <input className={inputCls} type="number" placeholder="0" value={food.carbs} onChange={e => setFood(mi, fi, 'carbs', e.target.value)} />
                                </div>
                                {meal.foods.length > 1 && (
                                  <button onClick={() => removeFood(mi, fi)} className="text-[#555] hover:text-red-400 transition-colors mt-auto pb-2">
                                    <Trash2 size={12} />
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className={labelCls}>Observações gerais</label>
                  <textarea className={`${inputCls} min-h-[80px] resize-y`} value={diet.notes} onChange={e => setDiet(d => ({ ...d, notes: e.target.value }))} />
                </div>

                <button onClick={saveDiet} disabled={saving}
                  className="flex items-center gap-2 px-6 py-3 bg-white text-black font-bold uppercase tracking-widest text-sm hover:bg-[#E5E5E5] transition-colors disabled:opacity-50 self-start"
                >
                  <Save size={15} /> {saving ? 'Salvando...' : saved ? 'Salvo!' : 'Salvar dieta'}
                </button>
              </div>
            </details>
          </div>
        )}

        {/* Diet assignment slide-over */}
        {showDietPanel && (
          <div className="fixed inset-0 z-[100] flex justify-end">
            <div className="absolute inset-0 bg-black/60" onClick={() => setShowDietPanel(false)} />
            <div className="relative w-full max-w-xl bg-[#0F0F0F] border-l border-white/10 h-full overflow-y-auto p-6 flex flex-col gap-6">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-bold uppercase tracking-widest">
                  {editingAssignment ? 'Editar atribuição de dieta' : 'Atribuir Dieta'}
                </h2>
                <button onClick={() => setShowDietPanel(false)}><X size={18} /></button>
              </div>

              <div>
                <label className={labelCls}>Nome da atribuição</label>
                <input value={dietAssignForm.name} onChange={e => setDietAssignForm(f => ({...f, name: e.target.value}))} className={inputCls} placeholder="Ex: Dieta fase cutting — Outubro" />
              </div>

              <div>
                <label className={labelCls}>Template de dieta</label>
                <select value={dietAssignForm.templateId}
                  onChange={e => {
                    const tpl = dietTemplates.find(t => t.id === e.target.value);
                    setDietAssignForm(f => ({
                      ...f,
                      templateId: e.target.value,
                      meals: tpl?.meals ? JSON.parse(JSON.stringify(tpl.meals)) : [],
                    }));
                  }}
                  className={inputCls}>
                  <option value="">Selecionar template (opcional)...</option>
                  {dietTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Data início</label>
                  <input type="date" value={dietAssignForm.startDate} onChange={e => setDietAssignForm(f => ({...f, startDate: e.target.value}))} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Data fim</label>
                  <input type="date" value={dietAssignForm.endDate} onChange={e => setDietAssignForm(f => ({...f, endDate: e.target.value}))} className={inputCls} />
                </div>
              </div>

              {dietAssignForm.meals && dietAssignForm.meals.length > 0 && (
                <div>
                  <p className="text-xs text-[#555] uppercase tracking-widest mb-2">Refeições ({dietAssignForm.meals.length})</p>
                  <div className="flex flex-col gap-2">
                    {dietAssignForm.meals.map((meal, mi) => (
                      <div key={mi} className="bg-[#121212] border border-white/5 p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-medium">{meal.name || `Refeição ${mi + 1}`}</span>
                          {meal.time && <span className="text-xs text-[#555]">{meal.time}</span>}
                        </div>
                        <p className="text-xs text-[#555]">{meal.foods?.length || 0} alimentos</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                onClick={async () => {
                  try {
                    if (editingAssignment) {
                      const res = await axios.put(`${BACKEND_URL}/api/admin/students/${id}/diet-assignments/${editingAssignment}`, dietAssignForm, { headers });
                      setDietAssignments(prev => prev.map(a => a.id === editingAssignment ? res.data : a));
                    } else {
                      const res = await axios.post(`${BACKEND_URL}/api/admin/students/${id}/diet-assignments`, dietAssignForm, { headers });
                      setDietAssignments(prev => [res.data, ...prev]);
                    }
                    setShowDietPanel(false);
                  } catch (err) { setAlertMsg(err.response?.data?.detail || 'Erro ao salvar atribuição.'); }
                }}
                className="px-6 py-3 bg-white text-black font-bold uppercase tracking-wider text-sm hover:bg-[#E5E5E5] transition-colors"
              >
                {editingAssignment ? 'Salvar alterações' : 'Atribuir dieta'}
              </button>
            </div>
          </div>
        )}

        {/* ── MEDIDAS ── */}
        {tab === 'measurements' && (
          <div className="flex flex-col gap-8">
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xs font-bold uppercase tracking-widest text-[#A3A3A3] flex items-center gap-2">
                  <Ruler size={13} /> Histórico de medidas
                </h2>
                <button onClick={() => setShowMeasForm(v => !v)}
                  className="flex items-center gap-1 text-xs text-[#A3A3A3] hover:text-white border border-white/10 px-2 py-1 transition-colors"
                >
                  <Plus size={12} /> {showMeasForm ? 'Cancelar' : 'Nova medição'}
                </button>
              </div>

              {showMeasForm && (
                <form onSubmit={addMeasurement} className="bg-[#121212] border border-white/5 p-4 flex flex-col gap-3">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                      <label className={labelCls}>Peso (kg)</label>
                      <input type="number" step="0.1" className={inputCls} placeholder="70.5" value={newMeasurement.weight} onChange={e => setNewMeasurement(m => ({ ...m, weight: e.target.value }))} />
                    </div>
                    <div>
                      <label className={labelCls}>Altura (cm)</label>
                      <input type="number" step="0.1" className={inputCls} placeholder="175" value={newMeasurement.height} onChange={e => setNewMeasurement(m => ({ ...m, height: e.target.value }))} />
                    </div>
                    <div>
                      <label className={labelCls}>% Gordura</label>
                      <input type="number" step="0.1" className={inputCls} placeholder="18.5" value={newMeasurement.bodyFat} onChange={e => setNewMeasurement(m => ({ ...m, bodyFat: e.target.value }))} />
                    </div>
                    <div>
                      <label className={labelCls}>Massa muscular (kg)</label>
                      <input type="number" step="0.1" className={inputCls} placeholder="35" value={newMeasurement.muscleMass} onChange={e => setNewMeasurement(m => ({ ...m, muscleMass: e.target.value }))} />
                    </div>
                    <div>
                      <label className={labelCls}>Cintura (cm)</label>
                      <input type="number" step="0.1" className={inputCls} placeholder="82" value={newMeasurement.waist} onChange={e => setNewMeasurement(m => ({ ...m, waist: e.target.value }))} />
                    </div>
                    <div>
                      <label className={labelCls}>Quadril (cm)</label>
                      <input type="number" step="0.1" className={inputCls} placeholder="98" value={newMeasurement.hip} onChange={e => setNewMeasurement(m => ({ ...m, hip: e.target.value }))} />
                    </div>
                    <div>
                      <label className={labelCls}>Data</label>
                      <input type="date" className={inputCls} value={newMeasurement.date} onChange={e => setNewMeasurement(m => ({ ...m, date: e.target.value }))} />
                    </div>
                    <div>
                      <label className={labelCls}>Obs.</label>
                      <input type="text" className={inputCls} placeholder="Opcional" value={newMeasurement.notes} onChange={e => setNewMeasurement(m => ({ ...m, notes: e.target.value }))} />
                    </div>
                  </div>
                  <button type="submit" className="self-start px-5 py-2 bg-white text-black text-sm font-bold uppercase tracking-wider hover:bg-[#E5E5E5] transition-colors">
                    Adicionar
                  </button>
                </form>
              )}

              {profile.measurements && profile.measurements.length >= 2 && (() => {
                const chartData = [...profile.measurements]
                  .filter(m => m.date)
                  .sort((a, b) => a.date.localeCompare(b.date))
                  .slice(-12)
                  .map(m => ({
                    date: m.date,
                    peso: m.weight ? parseFloat(m.weight) : null,
                    gordura: m.bodyFat ? parseFloat(m.bodyFat) : null,
                  }));
                return (
                  <div className="bg-[#121212] border border-white/5 p-4 mb-4">
                    <p className="text-xs font-bold uppercase tracking-widest text-[#A3A3A3] mb-4">Evolução</p>
                    <ResponsiveContainer width="100%" height={220}>
                      <LineChart data={chartData} margin={{ top: 5, right: 30, left: 0, bottom: 5 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#222" />
                        <XAxis dataKey="date" tick={{ fontSize: 10, fill: '#555' }} tickLine={false} />
                        <YAxis yAxisId="peso" orientation="left" tick={{ fontSize: 10, fill: '#A3A3A3' }} tickLine={false} axisLine={false} unit="kg" />
                        <YAxis yAxisId="gordura" orientation="right" tick={{ fontSize: 10, fill: '#F97316' }} tickLine={false} axisLine={false} unit="%" />
                        <Tooltip contentStyle={{ background: '#121212', border: '1px solid #222', borderRadius: 0, fontSize: 12 }} labelStyle={{ color: '#A3A3A3' }} />
                        <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
                        <Line yAxisId="peso" type="monotone" dataKey="peso" stroke="#A3A3A3" strokeWidth={2} dot={{ r: 3 }} name="Peso (kg)" connectNulls />
                        <Line yAxisId="gordura" type="monotone" dataKey="gordura" stroke="#F97316" strokeWidth={2} dot={{ r: 3 }} name="Gordura (%)" connectNulls />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                );
              })()}

              {(!profile.measurements || profile.measurements.length === 0) ? (
                <div className="text-center py-10 text-[#555] text-sm bg-[#121212] border border-white/5">
                  Nenhuma medição registrada ainda.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left py-2 pr-3 text-xs text-[#555] uppercase tracking-widest font-medium">Data</th>
                        <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">Peso</th>
                        <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">Alt.</th>
                        <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium">% Gord.</th>
                        <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium hidden sm:table-cell">M. Musc.</th>
                        <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium hidden sm:table-cell">Cintura</th>
                        <th className="text-center py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium hidden sm:table-cell">Quadril</th>
                        <th className="text-left py-2 px-2 text-xs text-[#555] uppercase tracking-widest font-medium hidden md:table-cell">Obs.</th>
                        <th className="py-2 pl-2"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {profile.measurements.map((m, i) => (
                        <tr key={i} className="border-b border-white/5">
                          <td className="py-3 pr-3 text-[#A3A3A3]">{m.date || '—'}</td>
                          <td className="py-3 px-2 text-center text-[#A3A3A3]">{m.weight ? `${m.weight}kg` : '—'}</td>
                          <td className="py-3 px-2 text-center text-[#A3A3A3]">{m.height ? `${m.height}cm` : '—'}</td>
                          <td className="py-3 px-2 text-center text-[#A3A3A3]">{m.bodyFat ? `${m.bodyFat}%` : '—'}</td>
                          <td className="py-3 px-2 text-center text-[#A3A3A3] hidden sm:table-cell">{m.muscleMass ? `${m.muscleMass}kg` : '—'}</td>
                          <td className="py-3 px-2 text-center text-[#A3A3A3] hidden sm:table-cell">{m.waist ? `${m.waist}cm` : '—'}</td>
                          <td className="py-3 px-2 text-center text-[#A3A3A3] hidden sm:table-cell">{m.hip ? `${m.hip}cm` : '—'}</td>
                          <td className="py-3 px-2 text-[#555] text-xs hidden md:table-cell">{m.notes || ''}</td>
                          <td className="py-3 pl-2">
                            <button onClick={() => removeMeasurement(i)} className="text-[#555] hover:text-red-400 transition-colors">
                              <Trash2 size={13} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── CALENDÁRIO ── */}
        {tab === 'calendar' && (
          <div className="flex flex-col gap-6">
            <div>
              <h2 className="text-xs font-bold uppercase tracking-widest text-[#A3A3A3] mb-4">Calendário do aluno</h2>
              <CalendarView workoutAssignments={workoutAssignments} dietAssignments={dietAssignments} />
            </div>
          </div>
        )}

        {/* ── PERFIL ── */}
        {tab === 'profile' && (
          <div className="flex flex-col gap-8">

            {/* Status e Permissões */}
            <div className="flex flex-col gap-4">
              <h2 className="text-xs font-bold uppercase tracking-widest text-[#A3A3A3] flex items-center gap-2">
                <Shield size={13} /> Status e Acessos
              </h2>

              {/* Status selector */}
              <div>
                <label className={labelCls}>Status do aluno</label>
                <div className="flex border border-white/10 overflow-hidden w-fit">
                  {[['pending','Aguardando'], ['active','Ativo'], ['inactive','Inativo']].map(([val, label]) => (
                    <button key={val} type="button"
                      onClick={() => setProfile(p => ({ ...p, status: val }))}
                      className={`px-4 py-2 text-xs font-medium uppercase tracking-wider transition-colors ${(profile.status || 'pending') === val ? 'bg-white text-black' : 'bg-[#121212] text-[#555] hover:text-white'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Permissions toggles */}
              <div>
                <label className={labelCls}>Módulos liberados</label>
                {/* Disclaimer: no modules enabled */}
                {!Object.values(profile.permissions || {}).some(Boolean) && (
                  <div className="flex items-start gap-3 bg-yellow-500/5 border border-yellow-400/20 px-4 py-3 mt-2">
                    <span className="text-yellow-400 text-lg leading-none mt-0.5">⚠</span>
                    <p className="text-xs text-[#A3A3A3]">
                      Nenhum módulo liberado. Lembre-se de ativar os módulos adquiridos pelo aluno antes de salvar.
                    </p>
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                  {[
                    ['workout',      'Treino',        'Plano de treino personalizado'],
                    ['diet',         'Dieta',         'Plano alimentar personalizado'],
                    ['content',      'Conteúdos',     'Manuais e materiais broadcast'],
                    ['ebooks',       'E-books',       'Acesso à loja de e-books'],
                    ['measurements', 'Auto-medições', 'Aluno pode inserir suas próprias medidas'],
                  ].map(([key, label, desc]) => (
                    <button key={key} type="button"
                      onClick={() => setProfile(p => ({ ...p, permissions: { ...p.permissions, [key]: !p.permissions?.[key] } }))}
                      className={`flex items-center justify-between px-4 py-3 border text-left transition-colors ${profile.permissions?.[key] ? 'border-white/20 bg-white/5' : 'border-white/5 bg-[#121212]'}`}
                    >
                      <div>
                        <p className={`text-sm font-medium ${profile.permissions?.[key] ? 'text-white' : 'text-[#555]'}`}>{label}</p>
                        <p className="text-xs text-[#555] mt-0.5">{desc}</p>
                      </div>
                      <div className={`w-8 h-4 rounded-full transition-colors flex-shrink-0 ml-3 ${profile.permissions?.[key] ? 'bg-white' : 'bg-[#333]'}`}>
                        <div className={`w-3 h-3 rounded-full bg-black mt-0.5 transition-transform ${profile.permissions?.[key] ? 'translate-x-4 ml-0.5' : 'translate-x-0.5'}`} />
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Informações gerais */}
            <div className="flex flex-col gap-4">
              <h2 className="text-xs font-bold uppercase tracking-widest text-[#A3A3A3] flex items-center gap-2">
                <User size={13} /> Informações gerais
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Data de nascimento</label>
                  <input type="date" className={inputCls} value={profile.birthdate || ''} onChange={e => setProfile(p => ({ ...p, birthdate: e.target.value }))} />
                </div>
                <div>
                  <label className={labelCls}>Telefone</label>
                  <input type="text" className={inputCls} placeholder="(11) 99999-9999" value={profile.phone || ''} onChange={e => setProfile(p => ({ ...p, phone: e.target.value }))} />
                </div>
              </div>
              <div>
                <label className={labelCls}>Objetivo</label>
                <textarea className={`${inputCls} min-h-[80px] resize-y`} placeholder="Ex: Emagrecer, ganhar massa..." value={profile.goal || ''} onChange={e => setProfile(p => ({ ...p, goal: e.target.value }))} />
              </div>
              <div>
                <label className={labelCls}>Observações médicas</label>
                <textarea className={`${inputCls} min-h-[80px] resize-y`} placeholder="Alergias, medicamentos, restrições..." value={profile.medicalNotes || ''} onChange={e => setProfile(p => ({ ...p, medicalNotes: e.target.value }))} />
              </div>
              <button onClick={saveProfile} disabled={profileSaving}
                className="flex items-center gap-2 px-6 py-3 bg-white text-black font-bold uppercase tracking-widest text-sm hover:bg-[#E5E5E5] transition-colors disabled:opacity-50 self-start"
              >
                <Save size={15} /> {profileSaving ? 'Salvando...' : profileSaved ? 'Salvo!' : 'Salvar perfil'}
              </button>
            </div>

            {/* Saldo Calórico */}
            <div className="flex flex-col gap-4">
              <h2 className="text-xs font-bold uppercase tracking-widest text-[#A3A3A3] flex items-center gap-2">
                <Flame size={13} /> Saldo Calórico
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className={labelCls}>TMB (kcal/dia)</label>
                  <input type="number" className={inputCls} placeholder="1800"
                    value={profile.tmb || ''}
                    onChange={e => setProfile(p => ({ ...p, tmb: e.target.value }))}
                  />
                  <p className="text-xs text-[#555] mt-1">Taxa Metabólica Basal — manual por enquanto</p>
                </div>
                <div>
                  <label className={labelCls}>Nível de atividade</label>
                  <select className={inputCls}
                    value={profile.activityLevel || 'moderado'}
                    onChange={e => setProfile(p => ({ ...p, activityLevel: e.target.value }))}
                  >
                    {['sedentário','leve','moderado','ativo','muito ativo'].map(l => <option key={l}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Objetivo</label>
                  <select className={inputCls}
                    value={profile.caloricGoal || 'manutenção'}
                    onChange={e => setProfile(p => ({ ...p, caloricGoal: e.target.value }))}
                  >
                    {['emagrecimento','manutenção','ganho de massa'].map(g => <option key={g}>{g}</option>)}
                  </select>
                </div>
              </div>

              {/* Caloric balance display */}
              {(() => {
                const tmb = Number(profile.tmb) || 0;
                const dietCals = dietAssignments[0]?.meals?.reduce((s, m) => s + (m.foods || []).reduce((fs, f) => fs + (Number(f.calories) || 0), 0), 0) || 0;
                const exerciseCals = workoutAssignments.reduce((s, a) => s + (a.exercises || []).reduce((es, ex) => es + (Number(ex.estimatedCalories) || 0), 0), 0);
                const balance = dietCals - (tmb + exerciseCals);
                return tmb > 0 ? (
                  <div className="grid grid-cols-3 gap-3 mt-2">
                    {[
                      ['Ingestão', `${dietCals} kcal`, 'text-white'],
                      ['Gasto total', `${tmb + exerciseCals} kcal`, 'text-[#A3A3A3]'],
                      ['Saldo', `${balance > 0 ? '+' : ''}${balance} kcal`, balance > 200 ? 'text-green-400' : balance < -200 ? 'text-red-400' : 'text-yellow-400'],
                    ].map(([label, value, cls]) => (
                      <div key={label} className="bg-[#0A0A0A] border border-white/5 p-3 text-center">
                        <p className="text-xs text-[#555] uppercase tracking-widest mb-1">{label}</p>
                        <p className={`text-lg font-bold ${cls}`}>{value}</p>
                      </div>
                    ))}
                  </div>
                ) : null;
              })()}
            </div>

          </div>
        )}
      </div>

      {alertMsg && (
        <div className="fixed top-6 right-6 z-50 bg-[#121212] border border-red-500/30 text-red-400 text-sm px-4 py-3 flex items-center gap-3 shadow-xl">
          {alertMsg}
          <button onClick={() => setAlertMsg('')} className="text-[#555] hover:text-white ml-2">✕</button>
        </div>
      )}

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setConfirm(null)} />
          <div className="relative bg-[#121212] border border-white/10 p-6 w-full max-w-md">
            <p className="text-sm text-[#E5E5E5] mb-6 leading-relaxed">{confirm.message}</p>
            <div className="flex gap-3 justify-end flex-wrap">
              <button onClick={() => setConfirm(null)}
                className="px-4 py-2 text-sm text-[#555] hover:text-white transition-colors">
                Cancelar
              </button>
              {confirm.onAlt && (
                <button onClick={() => { confirm.onAlt(); setConfirm(null); }}
                  className="px-4 py-2 border border-white/10 text-[#A3A3A3] text-sm hover:text-white hover:border-white/30 transition-colors">
                  {confirm.altLabel || 'Depois'}
                </button>
              )}
              <button onClick={() => { confirm.onConfirm(); setConfirm(null); }}
                className={`px-4 py-2 text-sm transition-colors ${confirm.confirmStyle === 'success'
                  ? 'bg-green-500/10 border border-green-500/30 text-green-400 hover:bg-green-500/20'
                  : 'bg-red-500/10 border border-red-500/30 text-red-400 hover:bg-red-500/20'}`}>
                {confirm.confirmLabel || 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
